import { Component, computed, input, OnInit, output, signal } from '@angular/core';
import { CustomerService } from '../../../services/customer.service';
import { SeoWorkService } from '../../../services/seo-work.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { finalize, forkJoin, switchMap } from 'rxjs';
import Permissions from 'app/models/Permissions';
import FlagSources = Boo.Objects.Work.SeoMinute.Enums.FlagSources;
import FlagTypes = Boo.Objects.Work.SeoMinute.Enums.FlagTypes;
import { TabPanelDisplayMode } from 'app/components/enums/TabPanelDisplayMode';
import { DisplayStatuses } from './Models/display-statuses';
import { Periods } from './Models/periods';
import { TaskTypeWorkGroup, WorkPeriod } from './Models/models';
import SeoWorkTaskType = Boo.Objects.Work.SeoMinute.SeoWorkTaskType;
import WorkRequestDetail = Boo.Objects.Work.SeoMinute.WorkRequestDetail;
import { PriorityViewModel } from 'app/models/PriorityViewModel';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import Utils from '../../../shared/utils';
import { StoreService } from '../../../services/store.service';
import { DEFAULT_MAX_TASK_COUNT } from '../../../shared/constants';

@Component({
  selector: 'app-components-managecustomer-allocate-work',
  templateUrl: './allocate-work.component.html'
})
export class AllocateWorkComponent implements OnInit {
  customer = input<Boo.Objects.Customer>();
  partner = input<Boo.Objects.Partner>();
  partnerUser = input<Boo.Objects.PartnerUser>();
  user = input<Boo.Objects.User>();

  refreshCustomer = output<void>();

  checkingBlocks = false;
  itemsToDelete: WorkRequestDetail[] = [];

  isLoading = false;
  workGroups = signal<TaskTypeWorkGroup[]>([], {equal: () => false});
  previewingFuture = false;
  seoWorkSettings = signal<Boo.Objects.Work.SeoMinute.SeoWorkSettings>(null, {equal: () => false});
  selectedTab = 'Work Planner';
  previewWorkRequests = signal<Boo.Objects.Work.WorkRequest[]>([], {equal: () => false});
  nextRenewalDate: Date = new Date();
  websiteUrls: Boo.Objects.WebsiteUrl[] = [];
  pastWorkRequestDetails = signal<Boo.Objects.Work.SeoMinute.WorkRequestDetail[]>([], {equal: () => false});
  TabPanelDisplayMode: typeof TabPanelDisplayMode = TabPanelDisplayMode;

  forms = this.formBuilder.group({
    configurationChange: new FormControl<string>('', [Validators.required]),
  });

  /*
  WARNING:
  Very few of these computed signals are being used correctly. Computed signals should always be pure functions and work best for simple operations.
  Using signals like this is indicative of a larger problem with the architecture of the component.
  */

  taskTypes = computed<SeoWorkTaskType[]>(() => {
    const urlTaskTypes = [...new Set(this.seoWorkSettings().TaskTypes
      .flatMap(x => x.ValidationConfig?.UrlCannotBeDuplicatedRules ?? [])
      .flatMap(y => y.TaskTypeEnums))];
    const taskTypeOrderedUrls = urlTaskTypes.map(taskTypeId => {
      const orderedUrls = [...new Set(
        this.workGroups()
          .filter(x => x.taskTypeId === taskTypeId)
          .flatMap(x => x.workItems)
          .filter(x => this.statusTaskIsOpen(x))
          .map(x => x.WorkRequestWebsiteUrl)
          .concat(this.pastWorkRequestDetails().filter(x => x.WorkRequest.WorkTypeCreationId === taskTypeId).map(x => x.WorkRequestWebsiteUrl)))];

        return { TaskTypeId: taskTypeId, OrderedUrls: orderedUrls.filter(x => x != undefined) };
      }
    );

    this.seoWorkSettings().TaskTypes.forEach(validatedTaskType => {
      validatedTaskType.ValidationConfig?.UrlCannotBeDuplicatedRules.forEach(urlCannotBeDuplicated => {
        const restrictedUrls = [...new Set(taskTypeOrderedUrls.filter(x => urlCannotBeDuplicated.TaskTypeEnums.includes(x.TaskTypeId)).flatMap(x => x.OrderedUrls))];
        validatedTaskType.Flags = validatedTaskType.Flags.filter(flag => flag.Type !== FlagTypes.RestrictedUrls);

        if (restrictedUrls.length) {
          validatedTaskType.Flags.push({ Source: FlagSources.Validation, Type: FlagTypes.RestrictedUrls, Data: restrictedUrls, Message: urlCannotBeDuplicated.Message } as Boo.Objects.Work.SeoMinute.TaskTypeFlag);
        }
      });
    });

    return this.seoWorkSettings().TaskTypes;
  }, {equal: () => false});

  currentPeriod = computed<WorkPeriod>(() => {
    const workGroup = this.workGroups().filter(x => x.period === Periods.current);
    const periodDetails = this.seoWorkSettings().PeriodDetails.find(x => x.Period === Periods.current);

    workGroup.forEach(x => {
      const type = this.seoWorkSettings().TaskTypes.find(y => y.TaskTypeId === x.taskTypeId);
      x.isNonworkable = this.isNonworkable(type);
      x.isTemporarilyNonworkable = this.isTemporarilyNonworkable(type);
    });

    const workHasBeenGenerated = workGroup.some(x => x.workItems.some(y => y.WorkRequest.WorkOrderItemId));
    // generate virtual placeholder for validation purposes. This will not be persisted
    if (!workHasBeenGenerated && periodDetails.LocalBusinessCitationSubscriptionTaskCount > 0)
    {
      const lbcsTaskType = this.taskTypes().find(x => x.TaskTypeEnum === Boo.Objects.Enums.TaskTypeEnum.LocalBusinessCitationSubscription);

      workGroup.push(
        this.createTaskTypeWorkGroup(
          lbcsTaskType,
          periodDetails.Period,
          Array.from({ length: periodDetails.LocalBusinessCitationSubscriptionTaskCount }, () => this.createWorkRequestDetail(lbcsTaskType, periodDetails.Period)),
          false));
    }

    const workPeriod = {
      period: Periods.current,
      displayName: 'Current',
      taskTypeGroups: this.sortWorkGroups(workGroup),
      minutes: this.customer().SeoMinutesTotal,
      errors: []
    };

    workPeriod.errors = this.validatePeriod(workPeriod);
    return workPeriod;
  }, {equal: () => false})

  futurePeriods = computed<WorkPeriod[]>(() => {
    const lbcsTaskType = this.taskTypes().find(x => x.TaskTypeEnum === Boo.Objects.Enums.TaskTypeEnum.LocalBusinessCitationSubscription);
    return this.seoWorkSettings().PeriodDetails.filter(x => x.Period !== Periods.current).map((periodDetails, i) => {
      
      // Generate virtual placeholder for validation purposes. This will not be persisted
      const virtualWorkGroup = this.workGroups().filter(x => x.period === periodDetails.Period)
        .filter(x => x.taskTypeId !== Boo.Objects.Enums.TaskTypeEnum.LocalBusinessCitationSubscription);

      if (virtualWorkGroup.length && periodDetails.LocalBusinessCitationSubscriptionTaskCount)
      {
        virtualWorkGroup.push(
          this.createTaskTypeWorkGroup(
            lbcsTaskType,
            periodDetails.Period,
            Array.from({ length: periodDetails.LocalBusinessCitationSubscriptionTaskCount }, () => this.createWorkRequestDetail(lbcsTaskType, periodDetails.Period)),
            false));
      }

      virtualWorkGroup.forEach(x => {
        const type = this.seoWorkSettings().TaskTypes.find(y => y.TaskTypeId === x.taskTypeId);
        x.isNonworkable = this.isNonworkable(type);
        x.isTemporarilyNonworkable = this.isTemporarilyNonworkable(type);
      });

      const workPeriod: WorkPeriod = {
        period: periodDetails.Period,
        LocalBusinessCitationSubscriptionTaskCount: periodDetails.LocalBusinessCitationSubscriptionTaskCount,
        displayName: moment(this.nextRenewalDate).utc().add(i, 'month').format('L'),
        taskTypeGroups: this.sortWorkGroups(virtualWorkGroup),
        minutes: periodDetails.SeoMinutesTotal,
        errors: []
      };

      workPeriod.errors = this.validatePeriod(workPeriod);
      return workPeriod;
    });
  }, {equal: () => false});

  previewPeriods = computed<WorkPeriod[]>(() => {
    const workRequestDetails = this.previewWorkRequests().map(x => {
      const workRequestDetail = this.createWorkRequestDetail(this.taskTypes().find(y => y.TaskTypeId === x.WorkTypeCreationId), x.Period, x);

      if (x.WorkRequestId) {
        const t = this.workGroups().flatMap(x => x.workItems).find(y => y.WorkRequest.WorkRequestId === x.WorkRequestId);
        workRequestDetail.WorkRequestWebsiteUrl = t?.WorkRequestWebsiteUrl;
      }
      
      if (!workRequestDetail.WorkRequestWebsiteUrl && x.KeyValuePairs?.some(y => y.KeyValuePair.KeyId === Boo.Objects.Enums.KeyEnum.WebsiteUrlId)) {
        const websiteUrlId = +x.KeyValuePairs.find(y => y.KeyValuePair.KeyId === Boo.Objects.Enums.KeyEnum.WebsiteUrlId).KeyValuePair.Value;
        workRequestDetail.WorkRequestWebsiteUrl = this.websiteUrls.find(y => y.WebsiteUrlId === websiteUrlId)?.Url;
      }

      return workRequestDetail;
    });
    const maxPeriod = Math.max(...workRequestDetails.map(x => x.WorkRequest.Period)) + 1;
    const periods = Array.from({ length: maxPeriod }, (_, i) => i);
    const workGroups = this.generateTaskTypeGroupings(workRequestDetails);

    return periods.map((period) => {
      return {
        period: period,
        displayName: period == Periods.current ? 'Current' : moment(this.nextRenewalDate).utc().add(period - 1, 'month').format('L'),
        taskTypeGroups: this.sortWorkGroups(workGroups.filter(x => x.period === period)),
        minutes: null, // Not relevant
        errors: []
      }
    });
  }, {equal: () => false})

  workPlannerErrors = computed<string[]>(() => [...this.currentPeriod().errors, ...this.futurePeriods().flatMap(x => x.errors)]);

  constructor(
    private seoWorkService: SeoWorkService,
    private customerService: CustomerService,
    private sessionStorageService: SessionStorageService,
    private formBuilder: FormBuilder,
    private storeService: StoreService) { }

  ngOnInit(): void {
    this.isLoading = true;
    forkJoin([
      this.seoWorkService.getSeoWorkSettings(this.customer().CustomerId),
      this.seoWorkService.getOpenWorkRequests(this.customer().CustomerId),
      this.sessionStorageService.getPartnerUsers(),
      this.storeService.getSubscriptionsSnapshot(this.customer().CustomerId)
    ]).pipe(
      finalize(() => this.isLoading = false),
      switchMap(([seoWorkSettings, workRequestDetails, partnerUsers, subscriptionsSnapshot]) => {
        // If the user does not have permission for custom other tasks then then flag as unavailable
        if (!Utils.hasPermission(this.partner(), partnerUsers, Permissions.CanInsertCustomActionOther, this.user())) {
          const customTaskType = seoWorkSettings.TaskTypes.find(x => x.TaskTypeEnum === Boo.Objects.Enums.TaskTypeEnum.Custom);
          customTaskType.Flags.push({ Source: FlagSources.Permission, Type: FlagTypes.Unavailable, Message: 'Additional permission required.' } as Boo.Objects.Work.SeoMinute.TaskTypeFlag);
        }

        const onsiteRecommendationOrderingTypes = [
          Boo.Objects.Enums.TaskTypeEnum.OnsiteRecommendation,
          Boo.Objects.Enums.TaskTypeEnum.OnsiteRecommendationContentOnly,
          Boo.Objects.Enums.TaskTypeEnum.OnsiteRecommendationAndImplementation
        ];

        // If the user does not have permission for adding onsite recommendation tasks then flag as unavailable
        if (!Utils.hasPermission(this.partner(), partnerUsers, Permissions.CanCreateOnsiteRecommendationTasks, this.user())) {
          const osrTaskType = seoWorkSettings.TaskTypes.find(x => onsiteRecommendationOrderingTypes.includes(x.TaskTypeEnum));
          osrTaskType.Flags.push({ Source: FlagSources.Permission, Type: FlagTypes.Unavailable, Message: 'Additional permission required.' } as Boo.Objects.Work.SeoMinute.TaskTypeFlag);
        }

        this.websiteUrls = seoWorkSettings.WebsiteUrls;
        this.nextRenewalDate = subscriptionsSnapshot
          .filter(x => x.IntervalMonths > 0)
          .pop()
          ?.Next;
        this.seoWorkSettings.set(seoWorkSettings);
        this.workGroups.set(this.generateTaskTypeGroupings(workRequestDetails));
        this.isLoading = false;
        return this.seoWorkService.getOpenWorkRequestsForPastPeriods(this.customer().CustomerId);
      }))
      .subscribe({
        next: pastWorkRequestDetails => {
          this.pastWorkRequestDetails.set(pastWorkRequestDetails);
        },
        error: (err) => toastr.error(err)
      });
  }

  resetSeoMinutes(): void {
    bootbox.confirm('The customer\'s SEO minutes will be reset to match their most recent SEO product. Free minutes and roll over minutes will not be changed. Are you sure you want to reset minutes?',
      (result: any) => {
        if (result !== true) {
          return;
        }
        this.isLoading = true;
        this.customerService.resetSeoMinutes(this.customer().CustomerId)
          .pipe(
            finalize(() => this.isLoading = false)
          )
          .subscribe({
            next: () => {
              toastr.success('Customer\'s minutes were reset');
              this.refreshCustomer.emit();
            },
            error: (displayMessage) => toastr.error(displayMessage)
        });
      });
  }

  setItemQuantity(type: Boo.Objects.Work.SeoMinute.SeoWorkTaskType, period: number, amount: number): void {
    const group = this.getWorkGroup(type, period);

    if (amount === (group?.workItems.length ?? 0)
      || amount > (type.MaxCount ?? DEFAULT_MAX_TASK_COUNT)
      || amount < 0) {
      return;
    }

    if (amount === 0) {
      this.removeGroup(type, period);
    }
    else if (amount > (group?.workItems.length ?? 0)) {
      const items = Array.from({ length: amount - (group?.workItems.length ?? 0) }, () => this.createWorkRequestDetail(type, period));

      this.workGroups.update(groups => {
        if (!group) {
          groups.push(this.createTaskTypeWorkGroup(type, period, items, true));
        }
        else {
          group.workItems.push(...items);
        }

        return groups;
      });
    }
    else {
      const toDelete = group.workItems.filter(x => x.CanDelete)
        .slice(0, group.workItems.length - amount);

      this.workGroups.update(groups => {
        group.workItems = group.workItems.filter(x => !toDelete.includes(x));
        return groups;
      });

      this.itemsToDelete.push(...toDelete.filter(x => x.WorkRequest.WorkRequestId > 0));
    }
  }

  addItem(type: SeoWorkTaskType, period: number): void {
    if (type.IsCustom) {
      const existingGroup = this.getWorkGroup(type, period);
      if (existingGroup && existingGroup.workItems.length >= existingGroup.maxCount) {
        toastr.warning(`You can only add up to ${existingGroup.maxCount} ${type.Name} tasks per period.`);
        return;
      }

      const newItem = this.createWorkRequestDetail(type, period);

      PriorityViewModel.show(
        'app-components-managecustomer-workrequestedit', {
        customer: ko.observable(this.customer()),
        taskType: type,
        workRequestDetail: newItem,
        hasValidAccessDetails: this.seoWorkSettings().HasValidAccessDetails
      }).done(workRequest => {
        this.workGroups.update(groups => {
          let group = groups.find(x => x.taskTypeId === type.TaskTypeId && x.period === period);

          if (!group) {
            group = this.createTaskTypeWorkGroup(type, period, [], true);
            // Custom minutes live on WorkQuantity, not type.Minutes.
            group.taskTypeMinutes = workRequest.WorkRequest.WorkQuantity;
            groups.push(group);
          }

          group.workItems.push(workRequest);

          return groups;
        });
      });
    }
    else {
      this.setItemQuantity(type, period, (this.getWorkGroup(type, period)?.workItems.length ?? 0) + 1);
    }
  }

  removeItem(workRequest: WorkRequestDetail): void {
    if (!workRequest.CanDelete) {
      return;
    }

    this.workGroups.update(groups => {
      const group = groups.find(x => x.taskTypeId === workRequest.WorkRequest.WorkTypeCreationId && x.period === workRequest.WorkRequest.Period);
      group.workItems = group.workItems.filter(x => x !== workRequest);

      if (group.workItems.length === 0) {
        groups = groups.filter(x => x !== group);
      }

      return groups;
    });

    if (workRequest.WorkRequest.WorkRequestId > 0 && workRequest.WorkRequest.Period >= Periods.current) {
      this.itemsToDelete.push(workRequest);
    }
  }

  editItem(item: Boo.Objects.Work.SeoMinute.WorkRequestDetail): void {
    const taskType = this.taskTypes().find(x => x.TaskTypeId === item.WorkRequest.WorkTypeCreationId);
    PriorityViewModel.show(
      'app-components-managecustomer-workrequestedit',
      {
        customer: ko.observable(this.customer()),
        workRequestDetail: item,
        taskType: taskType,
        hasValidAccessDetails: this.seoWorkSettings().HasValidAccessDetails
      }
    );
  }

  checkWorkOrderBlocks(): void {
    this.checkingBlocks = true;
    this.seoWorkService.checkWorkOrderBlocks(this.customer().CustomerId)
      .subscribe({
        next: () => toastr.success('Rechecking Validation, Please allow up to five minutes for this process to complete.'),
        error: err => toastr.error(err)
      });
  }

  save(): void {
    if (this.forms.invalid || this.workPlannerErrors().length) {
      const errors = [...new Set(this.workPlannerErrors())];
  
      if (this.forms.invalid) {
        errors.push('Configuration change note is required.');
      }
  
      toastr.error(errors.join('<br />'));
      this.forms.get('configurationChange').updateValueAndValidity();
      return;
    }
  
    this.isLoading = true;
  
    this.seoWorkService.saveSeoWorkSettings(
      this.customer().CustomerId,
      this.seoWorkSettings(),
      this.workGroups().flatMap(x => x.workItems),
      this.itemsToDelete,
      this.forms.get('configurationChange').value
    )
    .pipe(
      switchMap(() => {
        //save succeeded; now fetch updated work requests
        //we must do this so that newly added workrequests have their ids.  Otherwise subsequent deletes fail. 
        return this.seoWorkService.getOpenWorkRequests(this.customer().CustomerId);
      }),
      finalize(() => this.isLoading = false)
    )
    .subscribe({
      next: (workRequestDetails) => {
        toastr.success('Settings saved successfully');
        this.forms.get('configurationChange').reset('');
        this.itemsToDelete = [];
        this.workGroups.set(this.generateTaskTypeGroupings(workRequestDetails));
      },
      error: (err) => {
        toastr.error('Failed to save: ' + (err.message || err));
      }
    });
  }


  previewFuture(): void {
    if (this.workPlannerErrors().length) {
      const errors = [...new Set(this.workPlannerErrors())];
      toastr.error(errors.join('<br />'));
      return;
    }

    this.isLoading = true;
    this.seoWorkService.forecastWork(
      this.customer().CustomerId,
      this.workGroups().flatMap(x => x.workItems))
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: futureWorkRequests => {
          this.previewWorkRequests.set(futureWorkRequests);
          this.previewingFuture = true;
        },
        error: (err) => toastr.error(err)
      });
  }

  stopPreview(): void {
    this.previewingFuture = false;
  }

  tabChanged(tab: string): void {
    this.selectedTab = tab;
  
    if (this.previewingFuture)
    {
      this.previewingFuture = false;
    }
  }

  deleteWorkRequest(workRequest: WorkRequestDetail): void {
    this.isLoading = true;
    this.seoWorkService.deleteSeoWorkRequest(workRequest)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: () => {
          toastr.success(`${workRequest.WorkDisplayName} - ${workRequest.OrderTaskId} deleted`);
          this.pastWorkRequestDetails.update(pastWorkRequestDetails => {
            return  [...pastWorkRequestDetails.filter(x => x.WorkRequest.WorkRequestId !== workRequest.WorkRequest.WorkRequestId)];
          });
          this.refreshCustomer.emit();
        },
        error: (error) => toastr.error(error)
      });
  }

  refreshTaskTypesAndPeriodDetails(): void {
    this.isLoading = true;
    this.seoWorkService.getSeoWorkSettings(this.customer().CustomerId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (seoWorkSettings) => this.seoWorkSettings.update(x => {
          x.TaskTypes = seoWorkSettings.TaskTypes;
          x.PeriodDetails = seoWorkSettings.PeriodDetails;
          return x;
        }),
        error: (err) => toastr.error(err)
      });
  }

  private getWorkGroup(type: SeoWorkTaskType, period: number): TaskTypeWorkGroup {
    return this.workGroups().find(x => x.taskTypeId === type.TaskTypeId && x.period === period);
  }

  private generateTaskTypeGroupings(workRequestDetails: WorkRequestDetail[]): TaskTypeWorkGroup[] {
    const group = workRequestDetails.reduce((acc: TaskTypeWorkGroup[], curr) => {
      const group = acc.find(x => x.taskTypeId === curr.WorkRequest.WorkTypeCreationId && x.period === curr.WorkRequest.Period);
      if (group) {
        group?.workItems.push(curr) 
      } else {
        const type = this.taskTypes().find(x => x.TaskTypeId === curr.WorkRequest.WorkTypeCreationId);
        acc.push({
          taskTypeId: curr.WorkRequest.WorkTypeCreationId,
          taskTypeName: curr.WorkDisplayName,
          taskTypeDescription: curr.WorkDescription,
          taskTypeMinutes: curr.WorkRequest.WorkQuantity,
          workItems: [curr],
          isManual: this.isTaskTypeManual(curr.WorkRequest.WorkTypeCreationId),
          period: curr.WorkRequest.Period,
          maxCount: type.MaxCount && type.MaxCount >= 0 ? type.MaxCount : DEFAULT_MAX_TASK_COUNT,
          isNonworkable: this.isNonworkable(type),
          isTemporarilyNonworkable: this.isTemporarilyNonworkable(type),
          canChangeQuantity: this.canChangeQuantity(type)
        });
      }
      return acc;
    }, []);

    return group;
  }

  private canChangeQuantity(type: SeoWorkTaskType): boolean {
    return type.TaskTypeEnum !== Boo.Objects.Enums.TaskTypeEnum.LocalBusinessCitationSubscription;
  }

  private isTaskTypeManual(taskTypeId: number): boolean {
    return this.taskTypes().find(x => x.TaskTypeId === taskTypeId)?.IsCustom;
  }

  private removeGroup(type: SeoWorkTaskType, period: number): void {
    this.itemsToDelete.push(...this.workGroups().find(x => x.taskTypeId === type.TaskTypeId && x.period === period)?.workItems
      .filter(x => x.WorkRequest.WorkRequestId > 0) ?? []);
    this.workGroups.update(groups => groups.filter(x => !(x.taskTypeId === type.TaskTypeId && x.period === period)));
  }

  private validatePeriod(period: WorkPeriod): string[] {
    const errors = [];
    const totalMinutes = period.taskTypeGroups.reduce((acc, group) => acc + group.workItems.length * group.taskTypeMinutes, 0);

    if (totalMinutes > period.minutes) {
      errors.push('Too many minutes allocated for period');
    }

    if ((period.period != Periods.current && period.taskTypeGroups.some(x => x.isNonworkable)) ||
        (period.period === Periods.current && period.taskTypeGroups.some(x => x.isNonworkable && x.workItems.some(x => x.WorkStatusDisplayName === DisplayStatuses.incomplete || x.WorkStatusDisplayName === DisplayStatuses.new)))) {
      errors.push('Deactivated task type in period');
    }

    return errors;
  }

  private sortWorkGroups(workGroups: TaskTypeWorkGroup[]): TaskTypeWorkGroup[] {
    return workGroups
      // sort by name
      .sort((a, b) => a.taskTypeName.localeCompare(b.taskTypeName))
      // sort by minutes per task
      .sort((a, b) => a.workItems[0].WorkRequest.WorkQuantity - b.workItems[0].WorkRequest.WorkQuantity)
      // sort by manual tasks
      .sort((a, b) => +a.isManual - +b.isManual);
  }

  private statusTaskIsOpen(x: Boo.Objects.Work.SeoMinute.WorkRequestDetail): unknown {
    return [0 /* new */, Boo.Objects.Enums.TaskStatusEnum.Pending, Boo.Objects.Enums.TaskStatusEnum.Locked, Boo.Objects.Enums.TaskStatusEnum.NeedsData].includes(x.StatusTaskStatusId);
  }

  private createWorkRequestDetail(taskType: SeoWorkTaskType, period: number, workRequest: Boo.Objects.Work.WorkRequest = null): WorkRequestDetail {
    return {
      WorkDisplayName: taskType.Name,
      WorkDescription: taskType.Description,
      WorkRequest: workRequest ?? {
        CustomerId: this.customer().CustomerId,
        Sku: 'SEO-MINUTE',
        Period: period,
        WorkTypeId: Fulfillment.Domain.WorkOrders.WorkTypes.Task,
        WorkTypeCreationId: taskType.TaskTypeId,
        WorkQuantity: taskType.Minutes,
        InsertedByUserId: this.user().UserId,
        WorkOrderItemId: null,
        InsertedDate: null,
        WorkRequestId: 0,
        WorkRequestFiles: [],
        KeyValuePairs: []
      },
      CanDelete: true,
      CanEdit: false,
      WorkOrderItemWorkId: 0,
      WorkOrderItemWorkTypeId: 0,
      WorkOrderItemStatusId: 0,
      OrderTaskId: 0,
      StatusTaskId: 0,
      StatusTaskStatusId: 0,
      StatusTaskTypeId: 0,
      WorkStatusDisplayName: DisplayStatuses.new

    } as WorkRequestDetail;
  }

  private createTaskTypeWorkGroup(type: SeoWorkTaskType, period: number, workItems: WorkRequestDetail[] = [], canChangeQuantity = true): TaskTypeWorkGroup {
    return {
      taskTypeId: type.TaskTypeId,
      taskTypeName: type.Name,
      taskTypeDescription: type.Description,
      taskTypeMinutes: type.Minutes,
      period: period,
      isManual: this.isTaskTypeManual(type.TaskTypeId),
      workItems: workItems,
      maxCount: type.MaxCount && type.MaxCount >= 0 ? type.MaxCount : DEFAULT_MAX_TASK_COUNT,
      isNonworkable: this.isNonworkable(type),
      isTemporarilyNonworkable: this.isTemporarilyNonworkable(type),
      canChangeQuantity: canChangeQuantity
    };
  }

  private isNonworkable(type: SeoWorkTaskType): boolean {
    return type.Flags.some(x => x.Type === FlagTypes.Nonworkable);
  }

  private isTemporarilyNonworkable(type: SeoWorkTaskType): boolean {
    return type.Flags.some(x => x.Type === FlagTypes.ExaustedForPeriod);
  }
}